import { Plus } from 'lucide-react'
import type { SchemaField } from './types'
import { SchemaFieldRow } from './schema-field-row'
import type { EntityIndex } from '../../hooks/use-entity-index'

interface SchemaTreeEditorProps {
  title: string
  fields: SchemaField[]
  onUpdate: (fields: SchemaField[]) => void
  onFieldRename?: (oldName: string, newName: string) => void
  entityIndex?: EntityIndex
}

export function SchemaTreeEditor({
  title,
  fields,
  onUpdate,
  onFieldRename,
  entityIndex,
}: SchemaTreeEditorProps) {
  function handleAddField() {
    onUpdate([...fields, { name: '', type: 'string', required: false }])
  }

  function handleFieldUpdate(index: number, updated: SchemaField) {
    const newFields = [...fields]
    newFields[index] = updated
    onUpdate(newFields)
  }

  function handleFieldDelete(index: number) {
    onUpdate(fields.filter((_, i) => i !== index))
  }

  return (
    <div>
      {/* Section header */}
      <div className="flex items-center justify-between py-2">
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          {title}
        </span>
        <button
          onClick={handleAddField}
          className="flex items-center gap-1 text-xs font-normal text-muted-foreground hover:text-foreground transition-colors"
        >
          <Plus size={14} />
          Add field
        </button>
      </div>

      {/* Empty state */}
      {fields.length === 0 ? (
        <div className="py-4 text-center">
          <p className="text-xs font-semibold text-muted-foreground">No fields defined</p>
          <p className="text-[10px] text-muted-foreground mt-1">
            Add fields to define this schema. Fields describe the shape of request or response data.
          </p>
          <button
            onClick={handleAddField}
            className="mt-2 text-xs text-primary hover:underline"
          >
            Add first field
          </button>
        </div>
      ) : (
        /* Field list */
        <div>
          {fields.map((field, i) => (
            <SchemaFieldRow
              key={`root-${i}`}
              field={field}
              depth={0}
              onUpdate={(updated) => handleFieldUpdate(i, updated)}
              onDelete={() => handleFieldDelete(i)}
              onRename={onFieldRename}
              entityIndex={entityIndex}
              autoFocus={field.name === '' && i === fields.length - 1}
            />
          ))}
        </div>
      )}
    </div>
  )
}
